/**
 * Add / edit tile dialog (S6) — preview-first: run the query read-only, keep
 * the trust strip visible, and let the autopilot pick the best tile type on
 * the config pane's type cards (the ONE type-choosing surface — auto-selected
 * after a preview unless the user already chose, see tileTypeSuggestions.ts).
 * TanStack Form owns the config values; SQL text and preview results live
 * beside it. On save the assembled tile goes through the store → repository
 * (the sole writer); the caller invalidates the tile's query if its data
 * inputs changed.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from '@tanstack/react-form';
import { useStore } from '@tanstack/react-store';
import { X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PopoverPortalDefaultProvider } from '@/components/ui/popover';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import { useConnectionStore } from '../../../../connections/store/connectionStore';
import { DATABASE_ENGINE_IDS, type DatabaseEngineId } from '@/core/engines';
import { useWorkspaceStore } from '../../../core/store/workspaceStore';
import type { SqlMonacoHandle } from '../../../common/utils/sqlMonacoTypes';
import type { Dashboard, DashboardTile, DashboardTileType } from '../../types/dashboardTypes';
import { isQueryBackedTile } from '../../types/dashboardTypes';
import { shapeSuggest } from '../../utils/shapeSuggest';
import { detectSpatialColumnNames } from '../../utils/spatialShapeDetect';
import {
  shouldAutoApplyBest,
  suggestionForType,
  type TileTypeOrigin,
} from './tileTypeSuggestions';
import {
  buildTileFromForm,
  formDefaultsFromTile,
  resolveChartFormColumnValues,
  suggestionPatch,
  type AddTileFormValues,
} from './addTileFormModel';
import { listKnownDatabases, resolveDefaultTileDatabase } from './addTileDatabaseDefault';
import {
  INITIAL_CHART_REFS_RESOLVE_SESSION,
  nextChartRefsResolveSession,
  type ChartRefsResolveSession,
} from './chartRefsResolveSession';
import { configShapeHint, submitBlocker } from './addTileValidation';
import { pickedColumnInfos, useTilePreview } from './useTilePreview';
import { AddTileDataPane, type AddTileSource } from './AddTileDataPane';
import { AddTileConfigPane } from './AddTileConfigPane';
import { TemplatesPane } from './TemplatesPane';
import { TemplateDetailPane } from './TemplateDetailPane';
import { useTemplateInsertion } from './useTemplateInsertion';
import { AiSourcePane } from './AiSourcePane';
import { VisualSourcePane } from './VisualSourcePane';
import { useAddTileVqe } from './useAddTileVqe';
import { deriveVqeFilterBindings } from './vqeBindingDerivation';
import type { TileProposal } from '../../services/aiTileProposal';
import type { DashboardTemplate } from '../../templates/templateTypes';
import { useBetaFeature } from '@/core/beta';

/** Visual-first (S8): new tiles open on the VQE pane; SQL stays one tab away. */
const DEFAULT_SOURCE: AddTileSource = 'visual';

/** Who last wrote the SQL buffer — drives save provenance + analytics. */
type SqlProvenance = 'sql' | 'template' | 'ai' | 'vqe';

export type AddTileSubmitMeta = {
  isEdit: boolean;
  autopilot: boolean;
  tileType: DashboardTile['tileType'];
  /**
   * Provenance for analytics (S7/S8/S9): who last wrote the SQL. Can't be
   * read off `activeSource` at submit time (template/AI insertion switches
   * straight back to 'sql' so the text stays editable) — a dedicated flag
   * tracks the last writer instead. Hand-editing on the SQL tab reclaims
   * 'sql' (a hand-edited query is no longer template/AI/VQE-authored).
   */
  source: SqlProvenance;
  templateCategory?: DashboardTemplate['category'];
};

const nextFreeY = (tiles: DashboardTile[]): number =>
  tiles.reduce((max, t) => Math.max(max, t.layout.y + t.layout.h), 0);

const DEFAULT_SIZE: Record<string, { w: number; h: number }> = {
  chart: { w: 6, h: 4 },
  stat: { w: 3, h: 2 },
  table: { w: 6, h: 4 },
  pivot: { w: 6, h: 4 },
  map: { w: 6, h: 4 },
  note: { w: 4, h: 2 },
};

export function AddTileDialog({
  open,
  onOpenChange,
  dashboard,
  editTile,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dashboard: Dashboard;
  /** Present = edit mode ("Configure…"). */
  editTile: DashboardTile | null;
  onSubmit: (tile: DashboardTile, meta: AddTileSubmitMeta) => Promise<boolean>;
}) {
  const { track } = useAnalytics();
  const [sql, setSql] = useState('');
  const [activeSource, setActiveSource] = useState<AddTileSource>(DEFAULT_SOURCE);
  const [autopilotUsed, setAutopilotUsed] = useState(false);
  const [lastSqlSource, setLastSqlSource] = useState<SqlProvenance>('sql');
  // Templates tab: the selected (not yet inserted) template. Selection only
  // PREVIEWS — `TemplateDetailPane` takes over the otherwise-empty right half
  // and its "Use template" button is the single insertion trigger.
  const [selectedTemplate, setSelectedTemplate] = useState<DashboardTemplate | null>(null);
  // Who set the current tile type this session — gates preview auto-select
  // (tileTypeSuggestions.ts). A ref: only read when suggestions change.
  const typeOriginRef = useRef<TileTypeOrigin>('none');
  const templateInsertion = useTemplateInsertion();
  const preview = useTilePreview();
  const activeConnections = useConnectionStore((s) => s.activeConnections);

  // S8 — the SQL editor stays mounted across source tabs; this handle is the
  // ONLY reliable programmatic write path (SqlMonaco is uncontrolled after
  // mount, so setSql alone no longer reaches the editor).
  const editorRef = useRef<SqlMonacoHandle | null>(null);
  // Marks handle-driven writes so onChange doesn't misattribute provenance.
  const programmaticSetRef = useRef(false);
  // Fresh activeSource for the (possibly stale-closured) Monaco onChange.
  const activeSourceRef = useRef(activeSource);
  activeSourceRef.current = activeSource;

  /** Programmatic SQL write: state + editor together, provenance untouched. */
  const applySql = (next: string) => {
    setSql(next);
    programmaticSetRef.current = true;
    try {
      editorRef.current?.setValue(next);
    } finally {
      programmaticSetRef.current = false;
    }
  };

  /** Monaco onChange — user typing ('sql' tab) or a VQE commit ('visual'). */
  const handleSqlChange = (next: string) => {
    setSql(next);
    if (programmaticSetRef.current) return;
    const source = activeSourceRef.current;
    if (source === 'visual') setLastSqlSource('vqe');
    else if (source === 'sql') setLastSqlSource('sql');
    // templates/ai: insertion handlers set their own provenance explicitly.
  };

  // MUST be reference-stable across renders: useForm re-runs `form.update`
  // every render, and an untouched form gets its values clobbered back to
  // `defaultValues` whenever the reference changes (arrays defeat the
  // shallow compare). Same reason ChartBuilderDialog memoizes its defaults.
  const initialDefaults = useMemo(() => formDefaultsFromTile(editTile), [editTile]);

  const form = useForm({
    defaultValues: initialDefaults,
    onSubmit: async ({ value }) => {
      const connection = activeConnections.find((c) => c.id === value.connectionId)?.connection;
      const engine = connection?.engine;
      const isVqeAuthored = lastSqlSource === 'vqe';
      const tile = buildTileFromForm({
        values: value,
        sql,
        existing: editTile,
        layout: {
          x: 0,
          y: nextFreeY(dashboard.tiles),
          ...(DEFAULT_SIZE[value.tileType || 'table'] ?? { w: 6, h: 4 }),
        },
        engine:
          engine === 'mssql' || engine === 'pgsql' || engine === 'mysql' ||
          engine === 'oracle' || engine === 'sqlite' || engine === 'kusto'
            ? engine
            : undefined,
        sourceKind: isVqeAuthored ? 'vqe' : 'sql',
        filterBindings: isVqeAuthored
          ? deriveVqeFilterBindings(vqe.structure, columnInfos)
          : undefined,
        columnInfos,
      });
      if (!tile) return;
      const ok = await onSubmit(tile, {
        isEdit: Boolean(editTile),
        autopilot: autopilotUsed,
        tileType: tile.tileType,
        source: lastSqlSource,
        templateCategory:
          lastSqlSource === 'template'
            ? templateInsertion.templateCategoryUsed ?? undefined
            : undefined,
      });
      if (ok) onOpenChange(false);
    },
  });
  const values = useStore(form.store, (s) => s.values);
  const set = (patch: Partial<AddTileFormValues>) => {
    for (const [key, val] of Object.entries(patch)) {
      form.setFieldValue(key as keyof AddTileFormValues, val as never);
    }
  };

  // (Re)initialize per open: form from the edited tile, SQL from its source;
  // edit mode auto-previews so the config sections know the column shape.
  useEffect(() => {
    if (!open) return;
    form.reset();
    if (!initialDefaults.connectionId) {
      // New tiles start on the first connected connection — one less step
      // for the common single-connection case; the selector can override.
      // Applied via setFieldValue (marks the form touched) so a later render
      // can't clobber it back to defaults.
      const first = useConnectionStore
        .getState()
        .activeConnections.find((c) => c.status === 'connected');
      if (first) form.setFieldValue('connectionId', first.id);
    }
    preview.reset();
    setAutopilotUsed(false);
    // Edit mode keeps the tile's stored type — treat it as an explicit pick
    // so the edit auto-preview below can never flip it.
    typeOriginRef.current = editTile ? 'user' : 'none';
    templateInsertion.reset();
    setSelectedTemplate(null);
    // Editing lands on the tab matching the tile's stored provenance: VQE
    // tiles reopen on the Visual pane (state ≡ SQL — the compact surface
    // rehydrates from the buffer); everything else lands on SQL, the truth.
    const editIsVqe = editTile && isQueryBackedTile(editTile) && editTile.source.kind === 'vqe';
    setActiveSource(editTile ? (editIsVqe ? 'visual' : 'sql') : DEFAULT_SOURCE);
    setLastSqlSource(editIsVqe ? 'vqe' : 'sql');
    const initialSql = editTile && isQueryBackedTile(editTile) ? editTile.source.sql : '';
    applySql(initialSql);
    if (editTile && isQueryBackedTile(editTile) && initialSql.trim()) {
      preview.run({
        connectionId: editTile.connectionId,
        database: editTile.database,
        sql: initialSql,
        dashboardId: dashboard.id,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open/editTile drive the reset
  }, [open, editTile]);

  const isNote = values.tileType === 'note';
  const columnInfos = useMemo(
    () => pickedColumnInfos(preview, values.resultSetIndex),
    [preview, values.resultSetIndex],
  );

  // `formDefaultsFromTile` seeds `chartX`/`chartY`/`chartSeries` from the
  // edited tile's persisted `ChartConfig` before any preview has run — there
  // is no column info yet to resolve a NAME-based (or whitespace-rekeyed)
  // reference against. Once THIS session's own preview completes, patch
  // those three fields so the pickers preselect the right column instead of
  // showing no selection.
  //
  // `AddTileDialog` is always mounted, so `preview` state survives close/
  // open across DIFFERENT tiles, and the open effect's `preview.reset()` +
  // `preview.run()` batch into one commit — the first render after reopening
  // still shows the PREVIOUS tile's `columnInfos`. Naively resolving as soon
  // as `columnInfos.length > 0` would resolve tile B's refs against tile A's
  // leftover columns (PR review, P2). `chartRefsResolveSession` gates on
  // this session having observed its own `running` status first, so only a
  // `done` produced by a run started after this dialog opened can resolve.
  //
  // Resolves the CURRENT form values (read fresh off `form.state`, not the
  // render-captured `values`), not the saved tile: the user can keep editing
  // the form while the preview is still running (e.g. change a Y aggregation
  // from `sum` to `avg`), and re-deriving from `editTile.chartConfig` once
  // the preview lands would silently discard that edit (PR review, P2).
  const chartTileId = editTile?.tileType === 'chart' ? editTile.id : null;
  const chartRefsSessionRef = useRef<ChartRefsResolveSession>(INITIAL_CHART_REFS_RESOLVE_SESSION);
  // Reset per dialog session AND per edited tile: if the edited tile were
  // swapped while the dialog stays open, tile B must not inherit tile A's
  // armed session (and A's leftover `done` preview columns).
  useEffect(() => {
    if (open) chartRefsSessionRef.current = INITIAL_CHART_REFS_RESOLVE_SESSION;
  }, [open, chartTileId]);
  useEffect(() => {
    if (!open) return;
    const { session, shouldResolve } = nextChartRefsResolveSession(chartRefsSessionRef.current, {
      previewStatus: preview.state.status,
      tileId: chartTileId,
      hasColumns: columnInfos.length > 0,
    });
    chartRefsSessionRef.current = session;
    if (shouldResolve) {
      const current = form.state.values;
      const resolvedFields = resolveChartFormColumnValues(
        { chartX: current.chartX, chartY: current.chartY, chartSeries: current.chartSeries },
        columnInfos,
      );
      if (resolvedFields) set(resolvedFields);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fires per preview/columnInfos change; `form`/`set` are render-scoped
  }, [open, chartTileId, preview.state.status, columnInfos]);

  // Spatial detection needs the source engine ('mock' included — e2e); the
  // DashboardEngineId narrowing at submit deliberately excludes it.
  const connectionEngine = activeConnections.find((c) => c.id === values.connectionId)?.connection
    .engine;
  const shapeEngine = (DATABASE_ENGINE_IDS as readonly string[]).includes(connectionEngine ?? '')
    ? (connectionEngine as DatabaseEngineId)
    : undefined;

  // Geometry columns of the picked preview set — the map suggestion (via
  // shapeSuggest below), the map config section, and the map shape hint all
  // read this ONE detection pass.
  const spatialColumnNames = useMemo(() => {
    if (preview.state.status !== 'done') return [];
    const sets = preview.state.result.resultSets;
    const picked = sets[values.resultSetIndex] ?? sets[0];
    if (!picked) return [];
    return detectSpatialColumnNames(
      picked.columns,
      picked.previewRows as Array<Record<string, unknown>>,
      shapeEngine,
    );
  }, [preview.state, values.resultSetIndex, shapeEngine]);

  const hint = configShapeHint(values, columnInfos, preview.state.status !== 'idle', spatialColumnNames);
  const blocker = submitBlocker(values, sql);

  // Shape suggestions for the picked preview set — computed ONCE here, shared
  // by the type cards (badge + click auto-config) and the data pane's
  // healthy-empty note.
  const suggestions = useMemo(() => {
    if (preview.state.status !== 'done') return null;
    const sets = preview.state.result.resultSets;
    const picked = sets[values.resultSetIndex] ?? sets[0];
    return picked
      ? shapeSuggest({ columns: picked.columns, rows: picked.previewRows, engine: shapeEngine })
      : null;
  }, [preview.state, values.resultSetIndex, shapeEngine]);

  // Auto-select the best suggestion when a preview completes: fills the empty
  // config pane without a second click. Gated so an explicit pick (card
  // click, edit mode, template/AI insertion) is never overridden; a re-run
  // preview may re-fit only while the type still came from autopilot.
  useEffect(() => {
    if (!suggestions || !shouldAutoApplyBest(typeOriginRef.current)) return;
    set(suggestionPatch(suggestions.best));
    setAutopilotUsed(true);
    typeOriginRef.current = 'autopilot';
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fires per new suggestion set; `set` is render-scoped
  }, [suggestions]);

  /**
   * The type cards are the ONE type-choosing surface: a click applies the
   * matching suggestion's auto-config when the preview produced one for that
   * type (best or alternative), else it just sets the type. Either way it's
   * an explicit pick — autopilot won't override it later.
   */
  const handleSelectType = (type: DashboardTileType) => {
    const suggestion = suggestionForType(suggestions, type);
    if (suggestion) {
      set(suggestionPatch(suggestion));
      setAutopilotUsed(true);
    } else {
      set({ tileType: type });
      setAutopilotUsed(false);
    }
    typeOriginRef.current = 'user';
  };

  /**
   * Config-section edits (chart sub-kind, columns, thresholds…) claim the
   * type+config as the user's — a re-run preview must not clobber a hand-
   * tuned setup even when the type itself was auto-selected. Title edits and
   * data-pane sets (connection, result set, refresh) stay plain `set`.
   */
  const setFromConfigSection = (patch: Partial<AddTileFormValues>) => {
    typeOriginRef.current = 'user';
    set(patch);
  };

  // The FORM owns the tile's database (`values.database`, picked via the
  // chrome row's DatabaseSelector); this is the one value every consumer
  // reads — VQE schema pulls, starter live-pull, previews, template/AI
  // auto-previews, and the saved `tile.database`.
  const dialogDatabase = values.database || undefined;

  // Default resolution for the database field: configured → sqlite 'main' →
  // first listed → none (addTileDatabaseDefault.ts). Runs when the dialog
  // opens on a connection and again on every connection CHANGE; edit mode
  // keeps `tile.database` for the tile's own connection. The ref tracks the
  // connection the field was last resolved (or user-edited) against, so a
  // manual pick is never clobbered while the connection stays put.
  const dbResolvedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (open) dbResolvedForRef.current = null;
  }, [open]);
  const editQueryConnectionId =
    editTile && isQueryBackedTile(editTile) ? editTile.connectionId : null;
  useEffect(() => {
    if (!open) return;
    // Read FRESH form state, not the render-captured `values` — on open this
    // effect runs in the same pass as the reset effect above, whose
    // form.reset()/setFieldValue writes the render closure can't see yet.
    const connectionId = form.state.values.connectionId;
    if (dbResolvedForRef.current === connectionId) return;
    dbResolvedForRef.current = connectionId;
    if (editQueryConnectionId && connectionId === editQueryConnectionId) return;
    const connection = useConnectionStore
      .getState()
      .activeConnections.find((c) => c.id === connectionId)?.connection;
    if (!connectionId || !connection) {
      form.setFieldValue('database', '');
      return;
    }
    const sync = resolveDefaultTileDatabase({
      engine: connection.engine,
      configuredDatabase: connection.database,
      knownDatabases: [],
    });
    if (sync) {
      form.setFieldValue('database', sync);
      return;
    }
    form.setFieldValue('database', '');
    void listKnownDatabases(connectionId, connection.engine).then((knownDatabases) => {
      // Stale-async guard: apply only if the connection hasn't changed since.
      if (dbResolvedForRef.current !== connectionId) return;
      const resolved = resolveDefaultTileDatabase({
        engine: connection.engine,
        configuredDatabase: connection.database,
        knownDatabases,
      });
      if (resolved) form.setFieldValue('database', resolved);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-fires per connection change; form is stable
  }, [open, values.connectionId, editQueryConnectionId]);

  const vqeBetaEnabled = useBetaFeature('visual-query-editor');

  // S8 — SR-Edit pipeline over the dialog's buffer for the Visual pane.
  // Gated on `open` (empty connectionId → context stays null) so the resolver
  // document isn't kept alive while the dialog is closed.
  const vqe = useAddTileVqe({
    editorRef,
    sql,
    connectionId: open ? values.connectionId : '',
    database: dialogDatabase,
  });

  /** Promote to a Query tab in its Visual view, seeded with the dialog's SQL; dialog closes. */
  const handleOpenFullVqe = () => {
    // Dashboards and the Visual Query Editor are independent opt-ins, so this
    // dialog can be open while VQE is off. The tile builder's visual pane is
    // Dashboards' own surface and stays, but promoting to a VQE *tab* is the
    // surface the user opted out of — hand them the dialog's SQL pane instead
    // of a workspace they can't otherwise reach.
    if (!vqeBetaEnabled) {
      setActiveSource('sql');
      return;
    }
    const ws = useWorkspaceStore.getState();
    const newTabId = ws.addTab({
      type: 'query',
      title: values.title.trim() || `Query-${ws.getNextQueryNumber()}`,
      customTitle: values.title.trim() ? true : undefined,
      content: { query: sql, viewMode: 'visual', visualStatementOffset: 0 },
      connectionInfo: values.connectionId
        ? { connectionId: values.connectionId, database: dialogDatabase }
        : undefined,
    });
    ws.setActiveTab(newTabId);
    track('feature:dashboard_vqe_open_full', {});
    // The Visual Query Editor's own open funnel: a new Visual document.
    track('feature:vqe_open', { source: 'dashboard', seeded: sql.trim().length > 0 });
    onOpenChange(false);
  };

  const runPreview = () => {
    preview.run({
      connectionId: values.connectionId,
      database: dialogDatabase,
      sql,
      dashboardId: dashboard.id,
    });
  };

  const handleUseTemplate = (template: DashboardTemplate) => {
    setLastSqlSource('template');
    setSelectedTemplate(null);
    // The template's declared default type is deliberate — the auto-preview
    // below must not let the autopilot flip it.
    typeOriginRef.current = 'user';
    templateInsertion.insertTemplate(template, {
      set,
      setSql: applySql,
      setActiveSource,
      preview,
      connectionId: values.connectionId,
      database: dialogDatabase,
      dashboardId: dashboard.id,
    });
  };

  /**
   * Ask-AI tab (S9): stage the accepted proposal exactly like a template
   * insertion — SQL as editable text, suggested tile type + title, drop back
   * onto the SQL tab, auto-run the preview (always read-only, S3 policy).
   * Uses `proposal.sql` directly rather than the `sql` state var, which would
   * still read stale until the next render.
   */
  const handleUseAiProposal = (proposal: TileProposal) => {
    applySql(proposal.sql);
    setLastSqlSource('ai');
    // The proposal's tile type is deliberate — same guard as templates.
    typeOriginRef.current = 'user';
    set({ tileType: proposal.tileType, title: proposal.title });
    setActiveSource('sql');
    preview.run({
      connectionId: values.connectionId,
      database: dialogDatabase,
      sql: proposal.sql,
      dashboardId: dashboard.id,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex max-h-[85vh] flex-col gap-3 sm:max-w-4xl"
        data-testid="add-tile-dialog"
      >
        {/* Modal-dialog + portalled-popover clash (see popover.tsx): the
            Dialog locks pointer-events on <body>, so body-portalled popovers
            (the VQE starter's table picker, the compact surface's add-filter/
            column/join pickers) would paint but swallow clicks. Render them
            inline instead — their fixed positioning still escapes the panes'
            overflow clipping. Same fix as FkLookupPicker. */}
        <PopoverPortalDefaultProvider portalled={false}>
        {/* Corner dismiss — this app's DialogContent has no built-in close
            button, so each dialog renders its own (same idiom as
            EnumValuesDetailsDialog / JsonStructureDetailsDialog). */}
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="absolute right-3 top-3 rounded-sm text-muted-foreground opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          aria-label="Close"
          title="Close (Esc)"
          data-testid="add-tile-close-x"
        >
          <X className="h-4 w-4" />
        </button>
        <DialogHeader>
          <DialogTitle>{editTile ? 'Configure tile' : 'Add tile'}</DialogTitle>
        </DialogHeader>
        <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-y-auto pr-1 md:grid-cols-2">
          {!isNote ? (
            <AddTileDataPane
              activeSource={activeSource}
              onSourceChange={setActiveSource}
              sourcePanes={{
                visual: (
                  <VisualSourcePane
                    vqe={vqe}
                    sql={sql}
                    connectionId={values.connectionId}
                    database={dialogDatabase}
                    onSeedSql={(seeded) => {
                      applySql(seeded);
                      setLastSqlSource('vqe');
                    }}
                    onShowSql={() => setActiveSource('sql')}
                    onOpenFull={handleOpenFullVqe}
                  />
                ),
                templates: (
                  <TemplatesPane
                    connectionId={values.connectionId}
                    selectedTemplateId={selectedTemplate?.id ?? null}
                    onSelectTemplate={setSelectedTemplate}
                  />
                ),
                ai: (
                  <AiSourcePane connectionId={values.connectionId} onApply={handleUseAiProposal} />
                ),
              }}
              connectionId={values.connectionId}
              onConnectionChange={(connectionId) => set({ connectionId })}
              database={dialogDatabase}
              onDatabaseChange={(database) => {
                // A manual pick pins the field for this connection — the
                // resolver ref already points at it, so nothing re-resolves.
                set({ database });
              }}
              sql={sql}
              onSqlChange={handleSqlChange}
              editorRef={editorRef}
              preview={preview}
              onPreview={runPreview}
              resultSetIndex={values.resultSetIndex}
              onResultSetIndexChange={(resultSetIndex) => set({ resultSetIndex })}
              refresh={{ mode: values.refreshMode, seconds: values.refreshSeconds }}
              onRefreshChange={(r) =>
                set(
                  r.mode === 'interval'
                    ? { refreshMode: 'interval', refreshSeconds: r.seconds }
                    : { refreshMode: 'manual' },
                )
              }
              suggestions={suggestions}
            />
          ) : (
            <div className="hidden text-xs text-muted-foreground md:block">
              Notes are dashboard-local Markdown — no connection, query or refresh needed.
            </div>
          )}
          {/* Templates tab with a selection takes the right half: before a
              template is inserted there is no query, so the type cards +
              tile preview have nothing to say — the detail (description,
              SQL, permissions, fit) does. "Back" restores the config pane. */}
          {activeSource === 'templates' && selectedTemplate && !isNote ? (
            <TemplateDetailPane
              template={selectedTemplate}
              onUse={handleUseTemplate}
              onBack={() => setSelectedTemplate(null)}
            />
          ) : (
          <AddTileConfigPane
            values={values}
            set={setFromConfigSection}
            columnInfos={columnInfos}
            spatialColumnNames={spatialColumnNames}
            suggestions={suggestions}
            onSelectType={handleSelectType}
            preview={preview}
            title={
              <Input
                className="h-7 text-sm"
                value={values.title}
                onChange={(e) => set({ title: e.target.value })}
                placeholder={values.tileType ? `New ${values.tileType}` : 'Tile title'}
                data-testid="add-tile-title-input"
              />
            }
          />
          )}
        </div>
        {hint && (
          <p className="text-xs text-warning" data-testid="add-tile-shape-hint">
            {hint}
          </p>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {/* WHY tooltip on a span wrapper: the Button's disabled styles set
              pointer-events-none, so a `title` on the button itself never
              shows while disabled (S24 F19). */}
          <span title={blocker ?? undefined}>
            <Button
              type="button"
              onClick={() => void form.handleSubmit()}
              disabled={Boolean(blocker)}
              className="disabled:bg-muted disabled:text-muted-foreground"
              data-testid="add-tile-submit"
            >
              {editTile ? 'Save tile' : 'Add tile'}
            </Button>
          </span>
        </DialogFooter>
        </PopoverPortalDefaultProvider>
      </DialogContent>
    </Dialog>
  );
}
